import FeatureGuard from "@components/featureGuard/FeatureGuard"
import { MapButtons } from "@components/map/mapComponents/MapButtons"
import useAddLayersDataToMap from "@components/map/utils/useAddLayersDataToMap"
import useAssignWindyMapDiv from "@components/map/windyMap/useAssignWindyMapDiv"
import useInitWindyMap from "@components/map/windyMap/useInitWindyMap"
import { MapOptions, WindyMapType } from "@components/map/windyMap/windyMap"
import { useAppDispatch, useAppSelector } from "@redux/app/hooks"
import { PlantInfoValidated } from "@redux/features/listOfVessels/listOfVesselsSlice"
import {
	getMapFiltersPanelState,
	updateMapFiltersPanelState,
} from "@redux/features/mapFiltersPanelState/mapFiltersPanelStateSlice"
import { getPanelState } from "@redux/features/mapSidePanelControl/mapSidePanelControlSlice"
import { getSelectedVessel } from "@redux/features/selectedVessel/selectedVesselSlice"
import MapFiltersFacilitiesContent from "@sop/MapPage/MapFiltersFacilitiesContent"
import MapFiltersFishContent, {
	type MapFiltersFishStorageKey,
} from "@sop/MapPage/MapFiltersFishContent"
import MapFiltersOilContent, {
	type MapFiltersOilStorageKey,
} from "@sop/MapPage/MapFiltersOilContent"
import { useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { IconButton, Tooltip } from "@ui-lib/index"
import { common } from "@utils/common"
import ErrorBoundary from "@utils/errorBoundary/ErrorBoundary"
import { DipaiModule, PlantObject, PortalFeature } from "api"
import { Dispatch, memo, SetStateAction, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { MdClear, MdClose, MdSearch } from "react-icons/md"
import ErrorMessageMap from "../mapComponents/ErrorMessageMap"
import { LayerData } from "../utils/mapLayers"
import {
	useSetMapBounds,
	useSetMapBoundsV2,
	useSetMapBoundsV3,
	type MapBoundsV3Input,
} from "../utils/useSetMapBounds"
import { PointCollection } from "../utils/utils"
import { getPlantDataForVessel } from "../utils/vesselMetadata"
import "./windyMap.css"
import { FacilitiesFiltersStorageKey } from "@sop/MapPage/MapFiltersFacilitiesContent"

type MapData<T> = {
	layersData: LayerData[]
	onSearchChange?: (searchString: string) => void
	searchString?: string | null
	setSearchString?: Dispatch<SetStateAction<string | null>>
	inputsForBoundCalc?: T
	customerName: string
	showGlobalFilter?: boolean
	options?: MapOptions
	isLoading?: boolean
	facilitiesFiltersStorageKey?: FacilitiesFiltersStorageKey
	oilFiltersStorageKey?: MapFiltersOilStorageKey
	fishFiltersStorageKey?: MapFiltersFishStorageKey
}

function useSyncWindyMapResize(
	windyMap: WindyMapType | undefined,
	mapRef: HTMLDivElement | null,
	isPanelOpen: boolean
) {
	useEffect(() => {
		if (windyMap === undefined) {
			return
		}

		const timeouts: ReturnType<typeof setTimeout>[] = []
		const animationFrames: number[] = []
		const triggerResize = () => {
			animationFrames.push(
				requestAnimationFrame(() => {
					windyMap.resizemap()
				})
			)
		}

		triggerResize()
		for (const delay of [100, 250, 500]) {
			timeouts.push(
				setTimeout(() => {
					triggerResize()
				}, delay)
			)
		}

		return () => {
			for (const timeoutId of timeouts) {
				clearTimeout(timeoutId)
			}
			for (const animationFrameId of animationFrames) {
				cancelAnimationFrame(animationFrameId)
			}
		}
	}, [windyMap, isPanelOpen])

	useEffect(() => {
		if (windyMap === undefined || mapRef === null) {
			return
		}

		const animationFrames: number[] = []
		const triggerResize = () => {
			animationFrames.push(
				requestAnimationFrame(() => {
					windyMap.resizemap()
				})
			)
		}

		triggerResize()

		if (typeof ResizeObserver === "undefined") {
			return () => {
				for (const animationFrameId of animationFrames) {
					cancelAnimationFrame(animationFrameId)
				}
			}
		}

		const observer = new ResizeObserver(() => {
			triggerResize()
		})

		observer.observe(mapRef)

		return () => {
			observer.disconnect()
			for (const animationFrameId of animationFrames) {
				cancelAnimationFrame(animationFrameId)
			}
		}
	}, [windyMap, mapRef])
}

export function MapComponentBoundsV1(
	props: MapData<
		| {
				shipData: PlantInfoValidated[]
				fishGeoJson: PointCollection
		  }
		| undefined
	>
) {
	return (
		<ErrorBoundary fallback={ErrorMessageMap}>
			<MapComponentBodyBounds1 {...props} />
		</ErrorBoundary>
	)
}
function MapComponentBodyBounds1({
	layersData,
	onSearchChange,
	searchString,
	setSearchString,
	inputsForBoundCalc,
	customerName,
	showGlobalFilter = true,
	options,
	isLoading,
	facilitiesFiltersStorageKey,
	oilFiltersStorageKey,
	fishFiltersStorageKey,
}: MapData<
	| {
			shipData: PlantInfoValidated[]
			fishGeoJson: PointCollection
	  }
	| undefined
>) {
	useAssignWindyMapDiv()
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const queryClient = useQueryClient()
	const navigate = useNavigate()
	const { isOpen } = useAppSelector(getPanelState)
	const [windyMap, setWindyMap] = useState<WindyMapType>()
	const [mapRef, setMapRef] = useState<HTMLDivElement | null>(null)
	const companyData = common.companyDataFromCompanyName.get(customerName)

	const mapFiltersPanelState = useAppSelector(getMapFiltersPanelState)

	useInitWindyMap(
		dispatch,
		queryClient,
		setWindyMap,
		t,
		windyMap,
		companyData?.module === DipaiModule.OPERATION2 || companyData?.module === DipaiModule.DIPAI
			? navigate
			: undefined,
		options
	)

	useSyncWindyMapResize(windyMap, mapRef, isOpen)

	const selectedVessel = useAppSelector(getSelectedVessel)
	const plantData = getPlantDataForVessel(selectedVessel, customerName)
	useAddLayersDataToMap(
		layersData,
		{ map: windyMap, options },
		undefined,
		customerName,
		plantData
	)
	useSetMapBounds(windyMap, inputsForBoundCalc)

	return (
		<>
			{/* WINDY MAP CONTAINER */}
			<div
				ref={(newRef) => setMapRef(newRef)}
				id="windyHolder"
				className={`z-0 h-full w-full`}
			/>
			{/* GLOBAL FILTER */}
			{showGlobalFilter === true &&
			onSearchChange !== undefined &&
			setSearchString !== undefined ? (
				<div
					id={"globalFilter"}
					className="absolute top-3.5 left-16 inline-block rounded-md border-2 border-[rgba(0,0,0,0.2)]"
				>
					<div className="absolute inset-x-1 inset-y-1 h-6 w-6">
						<MdSearch className={`h-6 w-6`} />
					</div>
					<input
						className="text-menu shadow-card hover:border-alert-focus focus:border-alert-focus h-8 rounded-sm bg-white px-8 focus:border-1 focus:outline-hidden"
						value={searchString ?? ""}
						onChange={(e) => {
							setSearchString(e.target.value)
							onSearchChange(e.target.value)
						}}
						placeholder={t("SEARCH VESSEL BY NAME")}
					/>
					{searchString !== "" && searchString !== undefined && (
						<div
							className="text-gray-666 absolute inset-y-1 right-1 h-6 w-6 cursor-pointer"
							onClick={() => {
								setSearchString("")
								onSearchChange("")
							}}
						>
							<MdClear className={`h-6 w-6`} />
						</div>
					)}
				</div>
			) : null}

			{/* MAP BUTTONS */}
			<MapButtons map={windyMap} />

			{/* FILTERS PANEL */}
			<FiltersPanelMemo
				dispatch={dispatch}
				isOpen={mapFiltersPanelState.isOpen}
				mapRef={mapRef}
				plantData={plantData}
				facilitiesStorageKey={facilitiesFiltersStorageKey}
			/>

			{/* MAPTILE ATTRIBUTION */}
			<a
				id="maptilerLink"
				href="https://www.maptiler.com"
				className="absolute bottom-4 left-4 z-999 hidden"
			>
				<img
					src="https://api.maptiler.com/resources/logo.svg"
					alt="MapTiler logo"
				/>
			</a>
		</>
	)
}

export const MapComponentBoundsV2 = memo(
	(
		props: MapData<
			| {
					shipData: PlantInfoValidated[]
					trackData:
						| GeoJSON.Feature<GeoJSON.Point, GeoJSON.GeoJsonProperties>[]
						| undefined
			  }
			| undefined
		>
	) => {
		return (
			<ErrorBoundary fallback={ErrorMessageMap}>
				<MapComponentBodyBounds2 {...props} />
			</ErrorBoundary>
		)
	}
)
function MapComponentBodyBounds2({
	layersData,
	onSearchChange,
	searchString,
	setSearchString,
	inputsForBoundCalc,
	customerName,
	showGlobalFilter = true,
	options,
	isLoading,
	facilitiesFiltersStorageKey,
}: MapData<
	| {
			shipData: PlantInfoValidated[]
			trackData: GeoJSON.Feature<GeoJSON.Point, GeoJSON.GeoJsonProperties>[] | undefined
	  }
	| undefined
>) {
	useAssignWindyMapDiv()
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const queryClient = useQueryClient()
	const navigate = useNavigate()
	const { isOpen } = useAppSelector(getPanelState)
	const [windyMap, setWindyMap] = useState<WindyMapType>()
	const [mapRef, setMapRef] = useState<HTMLDivElement | null>(null)
	const companyData = common.companyDataFromCompanyName.get(customerName)

	const mapFiltersPanelState = useAppSelector(getMapFiltersPanelState)

	useInitWindyMap(
		dispatch,
		queryClient,
		setWindyMap,
		t,
		windyMap,
		companyData?.module === DipaiModule.OPERATION2 || companyData?.module === DipaiModule.DIPAI
			? navigate
			: undefined,
		options
	)

	useSyncWindyMapResize(windyMap, mapRef, isOpen)

	const selectedVessel = useAppSelector(getSelectedVessel)
	const plantData = getPlantDataForVessel(selectedVessel, customerName)

	useAddLayersDataToMap(
		layersData,
		{ map: windyMap, options },
		undefined,
		customerName,
		plantData
	)
	useSetMapBoundsV2(windyMap, inputsForBoundCalc)

	return (
		<>
			{/* WINDY MAP CONTAINER */}
			<div
				ref={(newRef) => setMapRef(newRef)}
				id="windyHolder"
				className={`z-0 h-full w-full`}
			/>
			{/* GLOBAL FILTER */}
			{showGlobalFilter === true &&
			onSearchChange !== undefined &&
			setSearchString !== undefined ? (
				<div
					id={"globalFilter"}
					className="absolute top-3.5 left-16 z-100000 inline-block rounded-md border-2 border-[rgba(0,0,0,0.2)]"
				>
					<div className="absolute inset-x-1 inset-y-1 h-6 w-6">
						<MdSearch className={`h-6 w-6`} />
					</div>
					<input
						className="text-menu shadow-card hover:border-alert-focus focus:border-alert-focus h-8 rounded-sm bg-white px-8 focus:border-1 focus:outline-hidden"
						value={searchString ?? ""}
						onChange={(e) => {
							setSearchString(e.target.value)
							onSearchChange(e.target.value)
						}}
						placeholder={t("SEARCH VESSEL BY NAME")}
					/>
					{searchString !== "" && searchString !== undefined && (
						<div
							className="text-gray-666 absolute inset-y-1 right-1 h-6 w-6 cursor-pointer"
							onClick={() => {
								setSearchString("")
								onSearchChange("")
							}}
						>
							<MdClear className={`h-6 w-6`} />
						</div>
					)}
				</div>
			) : null}

			{/* MAP BUTTONS */}
			<MapButtons map={windyMap} />

			{/* FILTERS PANEL */}
			<FiltersPanelMemo
				dispatch={dispatch}
				isOpen={mapFiltersPanelState.isOpen}
				mapRef={mapRef}
				plantData={plantData}
				facilitiesStorageKey={facilitiesFiltersStorageKey}
			/>

			{/* MAPTILE ATTRIBUTION */}
			<a
				id="maptilerLink"
				href="https://www.maptiler.com"
				className="absolute bottom-4 left-4 z-999 hidden"
			>
				<img
					src="https://api.maptiler.com/resources/logo.svg"
					alt="MapTiler logo"
				/>
			</a>
		</>
	)
}

export function MapComponentBoundsV3(props: MapData<MapBoundsV3Input | undefined>) {
	return (
		<ErrorBoundary fallback={ErrorMessageMap}>
			<MapComponentBodyBounds3 {...props} />
		</ErrorBoundary>
	)
}
function MapComponentBodyBounds3({
	layersData,
	onSearchChange,
	searchString,
	setSearchString,
	inputsForBoundCalc,
	customerName,
	showGlobalFilter = true,
	options,
	isLoading,
	facilitiesFiltersStorageKey,
}: MapData<MapBoundsV3Input | undefined>) {
	useAssignWindyMapDiv()
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const queryClient = useQueryClient()
	const navigate = useNavigate()
	const { isOpen } = useAppSelector(getPanelState)
	const [windyMap, setWindyMap] = useState<WindyMapType>()
	const [mapRef, setMapRef] = useState<HTMLDivElement | null>(null)
	const companyData = common.companyDataFromCompanyName.get(customerName)

	const mapFiltersPanelState = useAppSelector(getMapFiltersPanelState)

	useInitWindyMap(
		dispatch,
		queryClient,
		setWindyMap,
		t,
		windyMap,
		companyData?.module === DipaiModule.OPERATION2 || companyData?.module === DipaiModule.DIPAI
			? navigate
			: undefined,
		options
	)

	useSyncWindyMapResize(windyMap, mapRef, isOpen)

	const selectedVessel = useAppSelector(getSelectedVessel)
	const plantData = getPlantDataForVessel(selectedVessel, customerName)
	useAddLayersDataToMap(
		layersData,
		{ map: windyMap, options },
		undefined,
		customerName,
		plantData
	)
	useSetMapBoundsV3(windyMap, inputsForBoundCalc)

	return (
		<>
			{/* WINDY MAP CONTAINER */}
			<div
				ref={(newRef) => setMapRef(newRef)}
				id="windyHolder"
				className={`z-0 h-full w-full`}
			/>
			{/* GLOBAL FILTER */}
			{showGlobalFilter === true &&
			onSearchChange !== undefined &&
			setSearchString !== undefined ? (
				<div
					id={"globalFilter"}
					className="absolute top-3.5 left-16 inline-block rounded-md border-2 border-[rgba(0,0,0,0.2)]"
				>
					<div className="absolute inset-x-1 inset-y-1 h-6 w-6">
						<MdSearch className={`h-6 w-6`} />
					</div>
					<input
						className="text-menu shadow-card hover:border-alert-focus focus:border-alert-focus h-8 rounded-sm bg-white px-8 focus:border-1 focus:outline-hidden"
						value={searchString ?? ""}
						onChange={(e) => {
							setSearchString(e.target.value)
							onSearchChange(e.target.value)
						}}
						placeholder={t("SEARCH VESSEL BY NAME")}
					/>
					{searchString !== "" && searchString !== undefined && (
						<div
							className="text-gray-666 absolute inset-y-1 right-1 h-6 w-6 cursor-pointer"
							onClick={() => {
								setSearchString("")
								onSearchChange("")
							}}
						>
							<MdClear className={`h-6 w-6`} />
						</div>
					)}
				</div>
			) : null}

			{/* MAP BUTTONS */}
			<MapButtons map={windyMap} />

			{/* FILTERS PANEL */}
			<FiltersPanelMemo
				dispatch={dispatch}
				isOpen={mapFiltersPanelState.isOpen}
				mapRef={mapRef}
				plantData={plantData}
				facilitiesStorageKey={facilitiesFiltersStorageKey}
			/>

			{/* MAPTILE ATTRIBUTION */}
			<a
				id="maptilerLink"
				href="https://www.maptiler.com"
				className="absolute bottom-4 left-4 z-999 hidden"
			>
				<img
					src="https://api.maptiler.com/resources/logo.svg"
					alt="MapTiler logo"
				/>
			</a>
		</>
	)
}

export const FiltersPanelMemo = memo(
	({
		isOpen,
		dispatch,
		plantData,
		mapRef,
		facilitiesStorageKey = "mapFiltersFacilities",
		oilFiltersStorageKey = "mapFiltersOil",
		fishFiltersStorageKey = "mapFilters",
	}: {
		isOpen: boolean
		dispatch: ReturnType<typeof useAppDispatch>
		plantData: PlantObject | undefined
		mapRef: HTMLDivElement | null
		facilitiesStorageKey?: FacilitiesFiltersStorageKey
		oilFiltersStorageKey?: MapFiltersOilStorageKey
		fishFiltersStorageKey?: MapFiltersFishStorageKey
	}) => {
		const { t } = useTranslation()
		return isOpen === true ? (
			<div
				style={{ maxHeight: mapRef !== null ? mapRef.clientHeight - 32 : undefined }}
				className="absolute top-3.75 left-14.5 z-401 flex w-91.5 flex-col gap-0.5 overflow-auto rounded-sm border-0 bg-white/100 p-0.5"
			>
				<div className="bg-dipai-secondary-901 flex h-10 min-h-10 w-full items-center justify-between px-2">
					<p className={"text-title5 font-bold text-white"}>{t("MAP FILTERS")}</p>
					<Tooltip customContent={t("CLOSE")}>
						<IconButton
							size={"sm"}
							variant={"text-light"}
							onClick={() =>
								dispatch(
									updateMapFiltersPanelState({
										isOpen: false,
									})
								)
							}
						>
							<IconButton.Icon>{MdClose}</IconButton.Icon>
						</IconButton>
					</Tooltip>
				</div>
				<div className={`flex flex-col overflow-auto`}>
					<FeatureGuard
						featureName={PortalFeature.FACILITIES}
						plant={plantData}
					>
						<MapFiltersFacilitiesContent facilitiesStorageKey={facilitiesStorageKey} />
					</FeatureGuard>
					<FeatureGuard
						featureName={PortalFeature.OIL_AND_GAS}
						plant={plantData}
					>
						<MapFiltersOilContent oilFiltersStorageKey={oilFiltersStorageKey} />
					</FeatureGuard>
					<FeatureGuard
						featureName={PortalFeature.FISH_FARM}
						plant={plantData}
					>
						<MapFiltersFishContent fishFiltersStorageKey={fishFiltersStorageKey} />
					</FeatureGuard>
				</div>
			</div>
		) : null
	}
)
